import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import './PaymentForm.css';

function PaymentForm({ product, checkoutSessionId, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Step 1: Create payment method
      const cardElement = elements.getElement(CardElement);
      
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: name,
          email: email,
        },
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      console.log('Payment Method created:', paymentMethod.id);

      // Check if this is a demo session (no real N8N backend)
      const isDemoMode = checkoutSessionId?.startsWith('demo_');

      if (isDemoMode) {
        // Demo mode - simulate successful payment
        console.log('Demo mode - simulating successful payment');
        await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate processing
        onSuccess({
          success: true,
          demo: true,
          payment_method_id: paymentMethod.id
        });
        return;
      }

      // Step 2: Send actual payment method to backend
      console.log('Sending payment method:', paymentMethod.id);

      // Step 3: Call n8n Complete Checkout with payment method
      let completeResponse;
      try {
        completeResponse = await axios.post(
          process.env.REACT_APP_N8N_COMPLETE_URL,
          {
            checkout_session_id: checkoutSessionId,
            payment_method_id: paymentMethod.id
          },
          {
            // Handle empty response body (N8N may return 200 with no body)
            transformResponse: [(data) => {
              if (!data || data === '') return { success: true };
              try {
                return JSON.parse(data);
              } catch (e) {
                return { success: true };
              }
            }]
          }
        );
      } catch (axiosError) {
        // If request succeeded but response parsing failed, treat as success
        if (axiosError.response?.status === 200) {
          completeResponse = { data: { success: true }, status: 200 };
        } else {
          throw axiosError;
        }
      }

      console.log('Payment response:', completeResponse.data);

      // Handle various success responses from N8N
      if (completeResponse.data?.success || completeResponse.status === 200) {
        onSuccess(completeResponse.data || { success: true });
      } else {
        throw new Error(completeResponse.data?.error || 'Payment failed');
      }

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const cardStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <div className="payment-form-overlay">
      <div className="payment-form-container">
        <div className="payment-header">
          <h2>Complete Purchase</h2>
          <button className="close-btn" onClick={onCancel} disabled={isProcessing}>×</button>
        </div>

        <div className="product-summary">
          <h3>{product.title}</h3>
          <p className="product-price">${product.price}</p>
          <p className="product-duration">{product.duration_months} month{product.duration_months > 1 ? 's' : ''} access</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={isProcessing}
            />
          </div>

          <div className="form-group">
            <label>Cardholder Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
              disabled={isProcessing}
            />
          </div>

          <div className="form-group">
            <label>Card Details</label>
            <div className="card-element-container">
              <CardElement options={cardStyle} />
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="payment-actions">
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className="btn-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!stripe || isProcessing}
              className="btn-pay"
            >
              {isProcessing ? 'Processing...' : `Pay $${product.price}`}
            </button>
          </div>

          <p className="test-card-info">
            Test card: 4242 4242 4242 4242 | Any future date | Any CVC
          </p>
        </form>
      </div>
    </div>
  );
}

export default PaymentForm;